/* eslint-disable max-lines, max-depth, max-statements, complexity */
/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from "vuex";
import atUtil from "../../../../../pages/individualActiveTest/individualActiveTestUtil";
import individualDiagnose from "../../../../../../share/util/individualDiagnose";
import loading from "../../../../../../share/components/vue/ProcessingIcon";
import { ERR_KEY, FLOW, RES_TYPE } from "../../../individualActiveTestConstants.js";
import { REQUEST_STATUS_COMPLETED } from '../../../../../../share/util/api/polling.js';

const MENUS = {
  ATID: "active_test_id",
  SID22: "sid0x22"
};

const AT_EXE = {
  STATUS: "status",
  MNTR_STATUS: "monitoring_status",
  USER_DATA: "user_data",
  RES_TYPE: "res_type",
  VAL: "value",
  REQUEST_RESULT: "request_result",
};

/** モーダルフェーズ定義 */
const PHASE = {
  CALIBRATION_CONFIRM: 'calibrationConfirm',
  CALIBRATION_IN_PROGRESS: 'calibrationInProgress',
  CALIBRATION_FAILURE: 'calibrationFailure',
  EXECUTE_CONFIRM_NO_CALIBRATION: 'executeConfirmNoCalibration',
  EXECUTE_CONFIRM_WITH_CALIBRATION: 'executeConfirmWithCalibration',
};

export default {
  name: "activeTestButtonPanel",
  data() {
    return {
      // ダイアログフラグ
      modal_requirement_SP01: false,
      // スピナー表示フラグ
      isLoading: false,
      // 画面フェイズ
      phase: null,
      // 実行確認表示(キャリブレーション有)実行中フラグ（スピナー制御用）
      isConfirmProcess: false,
    };
  },

  props: {
    // requirementId
    requirementId: { type: String, default: "", required: true },
    // 選択されたAT項目
    selectedAtFunc: { type: Object, required: true },
  },

  components: {
    "processing-view": loading,
  },

  computed: {
    ...mapState({
      netAppId: (state) => state.netAppConnector.pairingAppId,
    }),
    systemId() {
      return individualDiagnose.getCurrentSystemId(this);
    },
    activeTestId() {
      return this.selectedAtFunc[MENUS.ATID];
    },
    // OKボタン表示
    showButtonOk() {
      return this.phase !== PHASE.CALIBRATION_IN_PROGRESS;
    },
    // ダイアログのヘッダカラー
    headerColor() {
      switch (this.phase) {
        case PHASE.CALIBRATION_FAILURE:
          return 'danger';
        case PHASE.EXECUTE_CONFIRM_WITH_CALIBRATION:
          return 'success';
        default:
          return 'secondary';
      }
    },
    // ダイアログのヘッダ文言
    headerTitle() {
      const titleMap = {
        [PHASE.CALIBRATION_CONFIRM]: 'message.common_title_confirmation',
        [PHASE.CALIBRATION_IN_PROGRESS]: 'individual.common_title_execution',
        [PHASE.CALIBRATION_FAILURE]: 'message.common_title_error',
        [PHASE.EXECUTE_CONFIRM_NO_CALIBRATION]: 'message.common_title_confirmation',
        [PHASE.EXECUTE_CONFIRM_WITH_CALIBRATION]: 'message.common_title_success',
      };
      return this.$te(titleMap[this.phase]) ?
        this.$t(titleMap[this.phase]) :
        this.$t('message.common_title_confirmation');
    },
    // ダイアログ表示文言を返す
    displayInfo() {
      const messageMap = {
        [PHASE.CALIBRATION_CONFIRM]: "label_calibration_not_completed",
        [PHASE.CALIBRATION_IN_PROGRESS]: `label_calibration_in_progress`,
        [PHASE.CALIBRATION_FAILURE]: `label_calibration_not_completed_exit`,
        [PHASE.EXECUTE_CONFIRM_NO_CALIBRATION]: `label_active_test_execution`,
        [PHASE.EXECUTE_CONFIRM_WITH_CALIBRATION]: `label_calibration_completed`,
      };
      return this.$te(`individual.${messageMap[this.phase]}`) ?
        this.$t(`individual.${messageMap[this.phase]}`) : '';
    },
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    // ダイアログ OKボタン押下時処理
    onOkClicked() {
      switch (this.phase) {
        case PHASE.CALIBRATION_CONFIRM:
          this.confirmedCalibration();
          break;
        case PHASE.EXECUTE_CONFIRM_WITH_CALIBRATION:
        case PHASE.EXECUTE_CONFIRM_NO_CALIBRATION:
          this.closeWizard();
          break;
        case PHASE.CALIBRATION_FAILURE:
          this.closeWizard();
          this.$emit('at-failure');
          break;
      }
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_SP01 = false;
    },

    /** 初期表示処理 */
    init() {
      // SP01モーダル呼び出し
      this.modal_requirement_SP01 = true;
      // 待機
      this.standbyCalibration();
    },

    /** 待機 */
    async standbyCalibration() {
      await this.sp1DoRead(
        // 読出し値"00" : キャリブレーション確認表示へ遷移
        () => { this.phase = PHASE.CALIBRATION_CONFIRM; },
        // 読出し値"FF" : 実行確認表示(キャリブレーション無)へ遷移
        () => { this.phase = PHASE.EXECUTE_CONFIRM_NO_CALIBRATION; }
      );
    },

    /** キャリブレーション確認表示 */
    confirmedCalibration() {
      this.phase = PHASE.CALIBRATION_IN_PROGRESS;
      this.isLoading = true;
      this.$emit("do-start");
    },

    /** 実行確認表示(キャリブレーション有) */
    async confirmExecution() {
      this.isConfirmProcess = true;
      await this.sp1DoRead(
        // 読出し値"00" : ステータスエラー画面遷移
        (status) => {
          this.$emit("show-status-error", status);
          this.closeWizard();
        },
        // 読出し値"FF" : AT文言表示
        () => { this.phase = PHASE.EXECUTE_CONFIRM_WITH_CALIBRATION; }
      );
      this.isConfirmProcess = false;
    },

    /**
     * データ読出し共通処理
     * @param {*} response00Callback 読出し値"00"時コールバック
     * @param {*} responseFFCallback 読出し値"FF"時コールバック
     * @returns 
     */
    async sp1DoRead(response00Callback, responseFFCallback) {
      try {
        this.isLoading = true;
        // データ読み出し
        const response = await atUtil.doRead(this, this.selectedAtFunc[MENUS.SID22]);

        // バリデーション
        if (!response) return;
        if (
          !response.data ||
          !response.data[AT_EXE.STATUS] === REQUEST_STATUS_COMPLETED ||
          !response.data[AT_EXE.USER_DATA] ||
          response.data[AT_EXE.USER_DATA].length === 0 ||
          !response.data[AT_EXE.USER_DATA][AT_EXE.RES_TYPE] === RES_TYPE.OK
        ) {
          this.$emit("change-flow", FLOW.ERR);
          this.$emit("do-error-process", ERR_KEY.COMMON);
          this.closeWizard();
          return;
        }

        // 正常終了時
        const monitoringStatusData = atUtil.getMonitoringStatusData(response.data[AT_EXE.USER_DATA]);
        const status = atUtil.getMonitoringStatus(monitoringStatusData[AT_EXE.VAL], this.requirementId);
        if (status === "00") {
          response00Callback(status);
        } else if (status === "FF") {
          responseFFCallback();
        } else {
          this.$emit("change-flow", FLOW.ERR);
          this.$emit("do-error-process", ERR_KEY.COMMON);
          this.closeWizard();
        }
      } finally {
        this.isLoading = false;
      }
    },
  },

  mounted() {
    this.init();
  },
};
